import * as yup from 'yup';
// interfaces, helper
import {
  AGE_GROUP_AT_LEAST_ONE_PRIMARY_TEXT,
  AGE_GROUPS_TEXT,
  AGE_GROUP_TYPE_TEXT,
  BEADLETS_TEXT,
  BLEND_NAME_TEXT,
  BOTTLE_CAP_TEXT,
  BOTTLE_TYPE_TEXT,
  CO_MFG_COST_PERCENTAGE_TEXT,
  COLOR_TEXT,
  DV_CONSTRAINTS_TYPE_TEXT,
  EXCIPIENT_GENERATOR_TEXT,
  EXCIPIENT_TEXT,
  FACT_BOX_TEXT,
  FLAVOR_SYSTEM_TEXT,
  FORM_TEXT,
  FORMULA_NAME_TEXT,
  FORMULATION_TEXT,
  INNER_CAPSULE_POLYMER_TEXT,
  MINI_TABLET_COLOR_TEXT,
  MINI_TABLETS_FORMULATION_TEXT,
  MISCELLANEOUS_COST_TEXT,
  OUTER_CAPSULE_POLYMER_TEXT,
  OVERAGE_TEXT,
  PRODUCT_FORMULATION_TEXT,
  PRODUCT_TYPE_TEXT,
  PROTEIN_BASE_TEXT,
  SERVING_PER_CONTAINER,
  SERVING_SIZE_TEXT,
  SERVING_UNIT_TEXT,
  SWEETENER_SYSTEM_TEXT,
  SYRUP_BASE_TEXT,
  TABLET_COATING_TEXT,
  TABLET_TEXT,
  TYPE_OF_CAPSULE_POLYMER_TEXT,
  TYPE_TEXT,
  UNIT_TEXT,
  VALUE_TEXT,
} from 'constants/index';
import { ProductFormulationTypes } from 'generated/graphql';
import {
  AddNewFormulaFormType,
  BlendItemType,
  DosageFormulationFormType,
  FactBoxFormulationFormType,
  FormulaAgeGroupSelectType,
  FormulaInfoCardFormType,
  FormulaIngredientFormType,
  SaveFormulaAsDraftFormType,
  YupSchemaShape,
} from 'interfaces';
import { requiredMessage } from 'lib/helper';
import {
  notRequiredPercentage,
  notRequiredPositiveNumber,
  overageSchema,
  positiveNumberMax,
  requiredPositiveNumber,
  selectorSchema,
} from './commonValidations';

export const formulaIngredientsSchema = (isTypeOptional: boolean = false) =>
  yup.object().shape({
    value: requiredPositiveNumber(VALUE_TEXT, 5),
    unit: yup.string().required(requiredMessage(UNIT_TEXT)),
    overage: overageSchema(OVERAGE_TEXT),
    type: isTypeOptional ? yup.string() : yup.string().required(requiredMessage(TYPE_TEXT)),
  });

export const excipientSchema = yup.object().shape({
  excipientType: selectorSchema(EXCIPIENT_TEXT),
  excipientLabel: yup.string().required(requiredMessage(EXCIPIENT_GENERATOR_TEXT)),
});

export const addFormulaSchema = yup.object().shape<YupSchemaShape<AddNewFormulaFormType>>({
  name: yup.string().trim().required(requiredMessage(FORMULA_NAME_TEXT)),
  formulaIngredients: yup.array().of(formulaIngredientsSchema()),
});

export const formulaInfoCardSchema = yup.object().shape<YupSchemaShape<FormulaInfoCardFormType>>({
  name: yup.string().trim().required(requiredMessage(FORMULA_NAME_TEXT)),
});

export const subProductSchema = yup.object().shape<YupSchemaShape<DosageFormulationFormType>>({
  productSubType: yup.string().required(requiredMessage(FORM_TEXT)),
});

export const ageGroupSchema = yup.object().shape<YupSchemaShape<FormulaAgeGroupSelectType>>({
  ageGroupType: yup.string().required(requiredMessage(AGE_GROUP_TYPE_TEXT)),
  dvType: yup.string().required(requiredMessage(DV_CONSTRAINTS_TYPE_TEXT)),
  servingSize: positiveNumberMax(SERVING_SIZE_TEXT, 10000),
  servingUnit: yup.string().required(requiredMessage(SERVING_UNIT_TEXT)),
  servingContainer: positiveNumberMax(SERVING_PER_CONTAINER, 10000),
});

const buildDosageSchema = (isBottleCapRequired: boolean = true) =>
  yup.object().shape<YupSchemaShape<DosageFormulationFormType>>({
    // Bottle cap is optional for liquid (it has no bottle) and required otherwise.
    bottleCap: isBottleCapRequired ? yup.string().required(requiredMessage(BOTTLE_CAP_TEXT)) : yup.string(),
    bottleType: isBottleCapRequired ? yup.string().required(requiredMessage(BOTTLE_TYPE_TEXT)) : yup.string(),
    description: yup.string().trim().default(''),
    productType: yup.string().required(requiredMessage(PRODUCT_TYPE_TEXT)),
    productFormulationType: selectorSchema(PRODUCT_FORMULATION_TEXT),

    coMfgCostType: yup.string().trim(),
    coMfgCostPercentage: notRequiredPercentage(CO_MFG_COST_PERCENTAGE_TEXT, 0.01, 99.99),
    miscellaneousCost: notRequiredPositiveNumber(MISCELLANEOUS_COST_TEXT, 5, true),
    ageGroups: yup
      .array()
      .of(ageGroupSchema)
      .min(1, requiredMessage(AGE_GROUPS_TEXT))
      .max(4, `${AGE_GROUPS_TEXT} should not be more than 4`)
      .test('at-least-one-primary', AGE_GROUP_AT_LEAST_ONE_PRIMARY_TEXT, (groups) =>
        (groups ?? []).some((g) => g?.ageGroupType === 'Primary' || g?.ageGroupType === 'PRIMARY'),
      ),
  });

const dosageSchema = buildDosageSchema();

export const factBoxSchema = (isTypeOptional: boolean = false) => {
  return yup.object().shape<YupSchemaShape<FactBoxFormulationFormType>>({
    factBoxType: yup.string().required(requiredMessage(FACT_BOX_TEXT)),
    formulaIngredients: yup.array().of(formulaIngredientsSchema(isTypeOptional)),
    capOverride: yup.boolean(),
  });
};

export const formulaIngredientSchema = (isTypeOptional: boolean = false) => {
  return yup.object().shape<YupSchemaShape<FormulaIngredientFormType>>({
    formulaIngredients: yup.array().of(formulaIngredientsSchema(isTypeOptional)),
    capOverride: yup.boolean(),
  });
};

export const coatingSchema = yup.object().shape<YupSchemaShape<DosageFormulationFormType>>({
  tabletCoating: yup.string(),
});

const flavorAndSweetenerSchema = yup.object().shape<YupSchemaShape<DosageFormulationFormType>>({
  flavorType: yup.string(),
  flavorSystem: selectorSchema(FLAVOR_SYSTEM_TEXT, false),

  sweetenerType: yup.string(),
  sweetenerSystem: selectorSchema(SWEETENER_SYSTEM_TEXT, false),
});

const powderSchema = (isFactBox: boolean = false) => {
  return yup
    .object()
    .shape<YupSchemaShape<DosageFormulationFormType>>({
      factBoxType: isFactBox ? yup.string() : yup.string().required(requiredMessage(FACT_BOX_TEXT)),
    })
    .concat(flavorAndSweetenerSchema)
    .concat(dosageSchema);
};

const liquidSchema = (isFactBox: boolean = false) => {
  return yup
    .object()
    .shape<YupSchemaShape<DosageFormulationFormType>>({
      factBoxType: isFactBox ? yup.string() : yup.string().required(requiredMessage(FACT_BOX_TEXT)),
    })
    .concat(flavorAndSweetenerSchema)
    .concat(buildDosageSchema(false));
};

const gummiesSchema = (isFactBox: boolean = false) => {
  return yup
    .object()
    .shape<YupSchemaShape<DosageFormulationFormType>>({
      factBoxType: isFactBox ? yup.string() : yup.string().required(requiredMessage(FACT_BOX_TEXT)),
      sandCoating: yup.string(),
      productSize: selectorSchema(SYRUP_BASE_TEXT),
    })
    .concat(dosageSchema)
    .concat(coatingSchema)
    .concat(subProductSchema)
    .concat(flavorAndSweetenerSchema);
};

const tabletSchema = yup.object().shape<YupSchemaShape<DosageFormulationFormType>>({}).concat(dosageSchema);

const capsuleSchema = yup
  .object()
  .shape<YupSchemaShape<DosageFormulationFormType>>({
    capsuleType: yup.string().required(requiredMessage(TYPE_OF_CAPSULE_POLYMER_TEXT)),
  })
  .concat(subProductSchema);

const colorCoatingSchema = yup
  .object()
  .shape<YupSchemaShape<DosageFormulationFormType>>({
    coatingColor: yup.string().required(requiredMessage(COLOR_TEXT)),
    // colorsHex: yup.array().of(yup.string()),
  })
  .concat(coatingSchema);

export const formulationSchema = (type: ProductFormulationTypes, isFactBox: boolean = false) => {
  switch (type) {
    // capsule cases

    case ProductFormulationTypes.PowderFilled:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(capsuleSchema)
        .concat(dosageSchema);

    case ProductFormulationTypes.LiquidFilled:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(capsuleSchema)
        .concat(dosageSchema);

    case ProductFormulationTypes.CapInCap:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          innerCapsuleType: yup.string().required(requiredMessage(INNER_CAPSULE_POLYMER_TEXT)),
          outerCapsuleType: yup.string().required(requiredMessage(OUTER_CAPSULE_POLYMER_TEXT)),
        })
        .concat(dosageSchema);

    case ProductFormulationTypes.LiquidFilledWithBeadlets:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          beadlets: yup.string().required(requiredMessage(BEADLETS_TEXT)),
        })
        .concat(capsuleSchema)
        .concat(dosageSchema);

    case ProductFormulationTypes.LiquidFilledWithTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          tabletType: yup.string().required(requiredMessage(TABLET_TEXT)),
          tabletCoating: yup.string(),
          coatingColor: yup.string().required(requiredMessage(TABLET_COATING_TEXT)),
        })
        .concat(capsuleSchema)
        .concat(dosageSchema);

    case ProductFormulationTypes.LiquidFilledWithMiniTabs:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          miniTabFormulation: yup.string().required(requiredMessage(MINI_TABLETS_FORMULATION_TEXT)),
          tabletCoating: yup.string(),
          coatingColor: yup.string().required(requiredMessage(MINI_TABLET_COLOR_TEXT)),
        })
        .concat(capsuleSchema)
        .concat(dosageSchema);

    // tablet cases

    case ProductFormulationTypes.SingleLayerTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(tabletSchema)
        .concat(colorCoatingSchema);

    case ProductFormulationTypes.BiLayeredTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          coatingColor2: yup.string().required(requiredMessage(COLOR_TEXT)),
        })
        .concat(tabletSchema)
        .concat(colorCoatingSchema);

    case ProductFormulationTypes.TriLayerTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          coatingColor2: yup.string().required(requiredMessage(COLOR_TEXT)),
          coatingColor3: yup.string().required(requiredMessage(COLOR_TEXT)),
        })
        .concat(tabletSchema)
        .concat(colorCoatingSchema);

    case ProductFormulationTypes.MiniTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          miniTabFormulation: yup.string().required(requiredMessage(FORMULATION_TEXT)),
        })
        .concat(tabletSchema)
        .concat(colorCoatingSchema);

    case ProductFormulationTypes.ChewableTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(tabletSchema)
        .concat(flavorAndSweetenerSchema);

    case ProductFormulationTypes.EffervescentTablet:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(tabletSchema)
        .concat(flavorAndSweetenerSchema);

    // powder cases

    case ProductFormulationTypes.ProteinPowder:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          proteinBase: yup.string().required(requiredMessage(PROTEIN_BASE_TEXT)),
        })
        .concat(powderSchema(isFactBox));

    case ProductFormulationTypes.PreOrPostPowder:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          veganType: yup.string(),
        })
        .concat(powderSchema(isFactBox));

    case ProductFormulationTypes.HydrationPowder:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(powderSchema(isFactBox));

    case ProductFormulationTypes.GreensOrRedsPowder:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({})
        .concat(powderSchema(isFactBox));

    case ProductFormulationTypes.BcaaPowder:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          veganType: yup.string(),
        })
        .concat(powderSchema(isFactBox));

    case ProductFormulationTypes.AllOthers:
      return yup
        .object()
        .shape<YupSchemaShape<DosageFormulationFormType>>({
          veganType: yup.string(),
        })
        .concat(powderSchema(isFactBox));

    // gummies

    case ProductFormulationTypes.NaturalSugarFree:
      return gummiesSchema(isFactBox);

    case ProductFormulationTypes.NonGmoSugar:
      return gummiesSchema(isFactBox);

    case ProductFormulationTypes.OrganicSugar:
      return gummiesSchema(isFactBox);

    case ProductFormulationTypes.SugarAlcohol:
      return gummiesSchema(isFactBox);

    case ProductFormulationTypes.SugarBased:
      return gummiesSchema(isFactBox);

    // liquid

    case ProductFormulationTypes.AllLiquids:
      return liquidSchema(isFactBox);

    default:
      return dosageSchema;
  }
};

export const blendItemSchema = yup.object().shape<YupSchemaShape<BlendItemType>>({
  name: yup.string().trim().required(requiredMessage(BLEND_NAME_TEXT)),
});

export const saveFormulaAsDraftSchema = yup.object().shape<YupSchemaShape<SaveFormulaAsDraftFormType>>({
  name: yup.string().trim().required(requiredMessage(FORMULA_NAME_TEXT)),
});
